// src/components/admin/corkBoard/sentiment/QuestionSentimentCard.tsx
import React from 'react';
import { Pressable, View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
// Import from the direct path (not the barrel), which re-exports GradientBackground
// → react-native-linear-gradient and breaks this component's Jest test.
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { SentimentBadge } from './SentimentBadge';
import { ThemeTagList } from './ThemeTagList';
import { QuotesAccordion } from './QuotesAccordion';
import { sentimentColor } from './sentimentColor';
import type { QuestionSentiment } from '@/types/sentiment';

interface Props {
  questionId: number;
  question: QuestionSentiment | undefined;
  awaitingAnalysisCount: number;
  onReclassify: () => void;
}

export const QuestionSentimentCard: React.FC<Props> = ({
  question,
  awaitingAnalysisCount,
  onReclassify,
}) => {
  const { redesign, semantic } = useAdminColors();
  const palette = sentimentColor(redesign);
  const summary = question?.summary;
  return (
    <GlassCard variant="soft" padding={12} style={styles.card}>
      <View style={styles.headerRow}>
        <Text style={[styles.title, { color: redesign.text.primary }]}>
          Sentiment
        </Text>
        {question?.status === 'pending' && <SentimentBadge status="pending" />}
        {question?.status === 'failed' && (
          <SentimentBadge status="failed" errorReason={question.errorReason} />
        )}
      </View>

      {summary ? (
        <>
          {summary.dominantSentiment && (
            <View style={styles.row}>
              <Text style={[styles.dominant, { color: redesign.text.primary }]}>
                Dominant: {summary.dominantSentiment}
              </Text>
              <View style={styles.distRow}>
                <Text style={[styles.distItem, { color: palette.positive }]}>
                  POSITIVE ({summary.distribution.positive})
                </Text>
                <Text
                  style={[styles.distDot, { color: redesign.text.secondary }]}
                >
                  {' · '}
                </Text>
                <Text style={[styles.distItem, { color: palette.neutral }]}>
                  NEUTRAL ({summary.distribution.neutral})
                </Text>
                <Text
                  style={[styles.distDot, { color: redesign.text.secondary }]}
                >
                  {' · '}
                </Text>
                <Text style={[styles.distItem, { color: palette.negative }]}>
                  NEGATIVE ({summary.distribution.negative})
                </Text>
              </View>
            </View>
          )}
          <Text
            style={[styles.summary, { color: redesign.text.primary }]}
            numberOfLines={3}
            ellipsizeMode="tail"
          >
            {summary.summaryText}
          </Text>
          <ThemeTagList themes={summary.themes} />
          <QuotesAccordion quotes={summary.quotes} />
        </>
      ) : (
        <Text style={[styles.placeholder, { color: redesign.text.secondary }]}>
          {question?.status === 'pending' ? 'Analyzing…' : 'No sentiment yet'}
        </Text>
      )}

      {awaitingAnalysisCount > 0 && (
        <View style={styles.awaitingRow}>
          <Text style={{ color: redesign.text.secondary }}>
            {awaitingAnalysisCount} answers awaiting analysis
          </Text>
          <Pressable testID="reclassify-btn" onPress={onReclassify}>
            <Text
              style={[styles.reclassifyLink, { color: semantic.text.link }]}
            >
              · Re-classify
            </Text>
          </Pressable>
        </View>
      )}
    </GlassCard>
  );
};

const styles = StyleSheet.create({
  card: { marginVertical: 8 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  title: { fontWeight: '700', fontSize: 14 },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  dominant: { textTransform: 'capitalize', fontWeight: '600' },
  distRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' },
  distItem: { fontSize: 12, fontWeight: '700' },
  distDot: { fontSize: 12 },
  summary: { marginTop: 8 },
  placeholder: { fontStyle: 'italic', marginTop: 4 },
  awaitingRow: {
    flexDirection: 'row',
    marginTop: 8,
    alignItems: 'center',
    gap: 4,
  },
  reclassifyLink: { textDecorationLine: 'underline' },
});
